/**
 * PaymentSuccessPage — F-010 (ritorno da Stripe Checkout, pagamento riuscito).
 *
 * Route `/app/pratiche/:id/payment-success`. Tra il redirect di Stripe e la
 * conferma webhook passano alcuni secondi: la pagina fa **polling** dello stato
 * pratica (ogni 2.5s) finché esce da BOZZA (→ INVIATA), poi mostra la conferma.
 */
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { praticaStatusClient } from '@/features/pratica-status/lib/pratica-status-client';

export function PaymentSuccessPage() {
  return (
    <RequireAuth roles={['CLIENT']}>
      <AppLayout>
        <PaymentSuccessInner />
      </AppLayout>
    </RequireAuth>
  );
}

export function PaymentSuccessInner() {
  const { id } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ['pratiche', 'status', id, 'payment-success'],
    queryFn: () => praticaStatusClient.getStatus(id!),
    enabled: !!id,
    // Poll finché la pratica è ancora BOZZA (webhook non ancora arrivato).
    refetchInterval: (q) => {
      const stato = q.state.data?.stato;
      return stato && stato !== 'BOZZA' ? false : 2500;
    },
  });

  const stato = query.data?.stato;
  const confirmed = !!stato && stato !== 'BOZZA';

  return (
    <section className="mx-auto max-w-lg space-y-6">
      <Card>
        <CardContent className="space-y-4 pt-6 text-center">
          {query.isError ? (
            <>
              <AlertCircle className="mx-auto h-12 w-12 text-destructive" aria-hidden="true" />
              <h1 className="text-2xl font-semibold">Pagamento ricevuto</h1>
              <p className="text-muted-foreground">
                Non riusciamo a verificare lo stato della pratica in questo momento.
                Controlla tra poco nelle tue pratiche.
              </p>
            </>
          ) : confirmed ? (
            <>
              <CheckCircle2 className="mx-auto h-12 w-12 text-primary" aria-hidden="true" />
              <h1 className="text-2xl font-semibold">Pagamento ricevuto</h1>
              <p className="text-muted-foreground" aria-live="polite">
                La pratica è stata inviata al nostro team e riceverai una conferma via
                email a breve. Riceverai anche la ricevuta di pagamento da Stripe.
              </p>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" aria-hidden="true" />
              <h1 className="text-2xl font-semibold">Stiamo confermando il pagamento…</h1>
              <p className="text-muted-foreground" aria-live="polite">
                Il pagamento è andato a buon fine. Stiamo registrando l'invio della
                pratica, ci vuole qualche secondo.
              </p>
            </>
          )}

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-center">
            {confirmed && id ? (
              <Button asChild>
                <Link to={`/app/pratiche/${id}`}>Vai alla pratica</Link>
              </Button>
            ) : null}
            <Button asChild variant="outline">
              <Link to="/pratiche">Le mie pratiche</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {!confirmed && !query.isError ? (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <AlertTitle>Attendere</AlertTitle>
          <AlertDescription>
            Non chiudere questa pagina: si aggiornerà da sola appena la pratica è
            registrata.
          </AlertDescription>
        </Alert>
      ) : null}
    </section>
  );
}
